<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <div>
          <h1>{{ 'BankAccount_Title' | translateWF: 'Bank Accounts' }}</h1>
          <p class="page__subtitle">
            {{
              'BankAccount_Subtitle'
                | translateWF
                  : 'Company cash accounts, balances and
            transactions.'
            }}
          </p>
        </div>
        <div class="page__title_buttons">
          <p-button [label]="'Export' | translateWF: 'Export'" icon="pi pi-download" variant="outlined" severity="secondary" [disabled]="true" />
          <p-button [label]="'BankAccount_Add' | translateWF: 'Add account'" icon="pi pi-plus" (click)="redirectToDetails()" />
        </div>
      </div>
    </div>

    <!-- KPI cards -->
    <div class="cs-summary-grid">
      <p-card data-variant="summary" [header]="('BankAccount_TotalCash' | translateWF: 'Total cash') + ' · USD'">
        <ng-template pTemplate="subtitle">{{ totalCashUsd() | number: '1.2-2' }}</ng-template>
        <div>{{ usdCount() }} {{ usdCount() === 1 ? ('BankAccount_Account' | translateWF: 'account') : ('BankAccount_Accounts' | translateWF: 'accounts') }}</div>
        @if (hiddenBalanceCount() > 0) {
          <div class="hint-muted">
            {{ hiddenBalanceCount() }}
            {{
              'BankAccount_HiddenFromTotal'
                | translateWF
                  : 'hidden
          from this total'
            }}
          </div>
        }
      </p-card>
      <p-card data-variant="summary" [header]="('BankAccount_TotalCash' | translateWF: 'Total cash') + ' · HNL'">
        <ng-template pTemplate="subtitle">{{ totalCashHnl() | number: '1.2-2' }}</ng-template>
        <div>{{ hnlCount() }} {{ hnlCount() === 1 ? ('BankAccount_Account' | translateWF: 'account') : ('BankAccount_Accounts' | translateWF: 'accounts') }}</div>
        @if (hiddenBalanceCount() > 0) {
          <div class="hint-muted">
            {{ hiddenBalanceCount() }}
            {{
              'BankAccount_HiddenFromTotal'
                | translateWF
                  : 'hidden
          from this total'
            }}
          </div>
        }
      </p-card>
      <p-card data-variant="summary" [header]="'BankAccount_ActiveAccounts' | translateWF: 'Active accounts'">
        <ng-template pTemplate="subtitle">{{ activeCount() }}</ng-template>
        <div>{{ 'BankAccount_OfTotal' | translateWF: 'of' }} {{ totalCount() }} {{ 'BankAccount_Total' | translateWF: 'total' }}</div>
      </p-card>
      <p-card data-variant="summary" [attr.data-severity]="belowMinimumCount() > 0 ? 'danger' : null" [header]="'BankAccount_BelowMinimum' | translateWF: 'Below minimum'">
        <ng-template pTemplate="subtitle">{{ belowMinimumCount() }}</ng-template>
        <div>{{ 'BankAccount_NeedsAttention' | translateWF: 'needs attention' }}</div>
      </p-card>
    </div>

    <div class="cs-page-list-grid">
      @if (filterConfig() && showFilters()) {
        <div>
          <csl-dynamic-filters [filterConfig]="filterConfig()!" [state]="filterState" />
        </div>
      }

      <p-panel [header]="'BankAccount_Title' | translateWF: 'Bank Accounts'">
        <ng-template pTemplate="icons">
          <p-button
            [label]="(showFilters() ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters() ? 'Hide Filters' : 'Show Filters')"
            [icon]="showFilters() ? 'pi pi-filter-slash' : 'pi pi-filter'"
            (click)="showFilters.set(!showFilters())"
            variant="outlined"
            size="small"
          />
        </ng-template>
        <p-card data-variant="compact">
          <p-table [value]="accounts()" selectionMode="single" [lazy]="true" (onSort)="onSort($event)">
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="code">{{ 'BankAccount_Table_Code' | translateWF: 'Code' }} <p-sortIcon field="code" /></th>
                <th pSortableColumn="name">{{ 'BankAccount_Table_Name' | translateWF: 'Account' }} <p-sortIcon field="name" /></th>
                <th pSortableColumn="bankName">{{ 'BankAccount_Table_BankName' | translateWF: 'Bank' }} <p-sortIcon field="bankName" /></th>
                <th pSortableColumn="bankAccountNumber">
                  {{
                    'BankAccount_Table_AccountNumber'
                      | translateWF
                        : 'Account
                  number'
                  }}
                  <p-sortIcon field="bankAccountNumber" />
                </th>
                <th>{{ 'BankAccount_Table_Type' | translateWF: 'Type' }}</th>
                <th>{{ 'BankAccount_Table_Currency' | translateWF: 'Currency' }}</th>
                <th>{{ 'BankAccount_Table_Status' | translateWF: 'Status' }}</th>
                <th class="text-right">{{ 'BankAccount_Table_CurrentBalance' | translateWF: 'Current balance' }}</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-account>
              <tr (click)="redirectToDetails(account.id)" class="cursor-pointer">
                <td>
                  <a class="cs-table-link">{{ account.code }}</a>
                </td>
                <td class="font-medium">{{ account.name }}</td>
                <td>{{ account.bankName }}</td>
                <td class="account-number">{{ account.bankAccountNumber }}</td>
                <td>{{ typeLabel(account.type) }}</td>
                <td>{{ currencyLabel(account.currency) }}</td>
                <td>
                  <p-tag [severity]="statusSeverity(account.status)" [value]="statusLabel(account.status)" />
                </td>
                <td class="text-right" [class.balance--negative]="(account.currentBalance ?? 0) < 0">
                  @if (account.canViewBalance) {
                    {{ account.currentBalance | maskedAmount }}
                  } @else {
                    <span class="balance--masked" [pTooltip]="'BankAccount_BalanceHidden' | translateWF: 'You do not have permission to view this balance'">{{
                      account.currentBalance | maskedAmount
                    }}</span>
                  }
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="8">{{ 'BankAccount_NotFound' | translateWF: 'No bank accounts found' }}</td>
              </tr>
            </ng-template>
          </p-table>
          <p-paginator
            [rows]="pageSize()"
            [totalRecords]="totalRecords()"
            [first]="(currentPage() - 1) * pageSize()"
            (onPageChange)="onPageChange($event)"
            [rowsPerPageOptions]="[25, 50, 100]"
            class="mt-4"
          />
        </p-card>

        <ng-template pTemplate="footer">
          <span>
            {{ 'BankAccount_Showing' | translateWF: 'Showing' }} {{ firstRecord() }} {{ 'BankAccount_To' | translateWF: 'to' }} {{ lastRecord() }}
            {{ 'BankAccount_OfTotal' | translateWF: 'of' }} {{ totalRecords() }} {{ 'BankAccount_Accounts' | translateWF: 'accounts' }}
          </span>
        </ng-template>
      </p-panel>
    </div>
  </div>
</main>
